import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Input } from "@bednbody/ui/components/input";
import { RadioGroup, RadioGroupItem } from "@bednbody/ui/components/radio-group";
import { Spinner } from "@bednbody/ui/components/spinner";
import { Textarea } from "@bednbody/ui/components/textarea";
import { cn } from "@bednbody/ui/lib/utils";
import { WarningIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { type RiderSessionRow, useAssignableRiders } from "../rider-sessions.hooks";

/** REND-005's three-value domain. Not a free number — 0193 refuses anything else. */
const EXTENSION_CHOICES = [1, 2, 3] as const;

const timeOf = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleTimeString("en-PH", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: "Asia/Manila",
      })
    : "—";

/**
 * ISO instant → `datetime-local`'s value shape, in the BROWSER's own
 * timezone — same convention `BookingDetailActions.tsx`'s `corrected-start`
 * field uses: the round-trip back through `new Date(value).toISOString()` on
 * submit is what actually fixes the instant, not this formatting step.
 */
function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type Mode = "reassign" | "release" | "void" | "retime";

interface SessionOverrideDialogProps {
  session: RiderSessionRow | null;
  /** Superadmin only (0245) — hide the option rather than offer a 403. */
  canRetime: boolean;
  isPending: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onReassign: (input: { toRiderId: string; maxExtensionHours: 1 | 2 | 3; reason: string }) => void;
  onRelease: (input: { reason: string }) => void;
  onVoid: (input: { reason: string }) => void;
  /** 0245 — Superadmin only; the server 403s an Admin caller. */
  onRetime: (input: { startsAt: string; endsAt: string; reason: string }) => void;
}

/**
 * OQ-122 — hand an abandoned Rider Session to a backup, or return it to the pool.
 *
 * ═══ WHAT THIS DIALOG SAYS OUT LOUD, AND WHY ═══
 *
 * An operator reaching for this is working around the normal flow, in an
 * emergency, probably at 2 PM with a hub short of riders. Three things they must
 * not have to already know:
 *
 * 1. **This is an override of a written rule.** CORE-05B §2 says Admin must not
 *    do this. The client authorised it anyway. The banner says so, because
 *    someone should be able to tell this apart from an ordinary edit.
 * 2. **The extension belongs to the incoming Rider.** REND-005 puts the choice
 *    before the secure action, so the field is asked, never inherited.
 * 3. **Nothing here creates supply.** The Session keeps its ID, code, date and
 *    Dispatch Area (CORE-05B §4.11) — only who holds it changes.
 *
 * Release is a second, quieter action rather than a second dialog: it is the same
 * decision ("this Rider is not working this Session") with the replacement left
 * unnamed, and splitting it across two entry points would hide it.
 */
export function SessionOverrideDialog({
  session,
  canRetime,
  isPending,
  errorMessage,
  onClose,
  onReassign,
  onRelease,
  onVoid,
  onRetime,
}: SessionOverrideDialogProps) {
  const [mode, setMode] = useState<Mode>("reassign");
  const [riderId, setRiderId] = useState("");
  const [extension, setExtension] = useState<1 | 2 | 3>(2);
  const [reason, setReason] = useState("");
  const [retimeStartsAt, setRetimeStartsAt] = useState("");
  const [retimeEndsAt, setRetimeEndsAt] = useState("");

  const { data: riders, isLoading: ridersLoading } = useAssignableRiders(session?.hub_id ?? null);

  // A fresh Session is a fresh decision. Without this the previous Session's
  // rider and reason survive into the next dialog, which is how somebody
  // reassigns the wrong row with a reason that describes a different one.
  useEffect(() => {
    if (session) {
      setMode("reassign");
      setRiderId("");
      setExtension(2);
      setReason("");
      setRetimeStartsAt(toDatetimeLocal(session.starts_at));
      setRetimeEndsAt(toDatetimeLocal(session.ends_at));
    }
  }, [session?.id]);

  const currentHolder = session?.secured_rider_id ?? null;
  const options = (riders ?? []).filter((r) => r.id !== currentHolder);
  const trimmedReason = reason.trim();

  /**
   * Void is offered ONLY on manually created supply. A generated Session is
   * counted supply and CORE-05B §4.10 forbids removing it after the batch
   * commits — the server answers `NOT_EMERGENCY_SUPPLY`, so offering the option
   * would be offering a refusal.
   */
  const isEmergency = session?.rider_session_batches?.origin === "emergency_override";

  const retimeValid =
    retimeStartsAt !== "" &&
    retimeEndsAt !== "" &&
    new Date(retimeEndsAt) > new Date(retimeStartsAt);
  const canSubmit =
    trimmedReason.length > 0 &&
    !isPending &&
    (mode === "reassign" ? riderId !== "" : mode === "retime" ? retimeValid : true);

  return (
    <Dialog
      open={session !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{currentHolder ? "Reassign Session" : "Assign Session"}</DialogTitle>
          <DialogDescription>
            {session ? (
              <>
                Session {session.code} · {timeOf(session.starts_at)}–{timeOf(session.ends_at)}
                {session.hubs?.name ? ` · ${session.hubs.name}` : ""}
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs">
            <p className="text-muted-foreground">
              Currently held by{" "}
              <span className="font-medium text-foreground">
                {session?.staff?.full_name ?? "nobody — this Session is unclaimed"}
              </span>
            </p>
            <p className="mt-1 text-muted-foreground">
              The Session keeps its ID, code, date and Dispatch Area. Only who holds it changes.
            </p>
          </div>

          {/* Always rendered now: reassign + retime are both always offered,
              so there is never fewer than two options to choose between. */}
          <fieldset>
            <legend className="mb-2 block text-sm font-medium">Action</legend>
            <RadioGroup value={mode} onValueChange={(value) => setMode(value as Mode)}>
              {(
                [
                  ["reassign", "Give it to another Rider", "They start from Secured, as normal."],
                  ...(currentHolder
                    ? ([
                        [
                          "release",
                          "Return it to the pool",
                          "Any eligible Rider can secure it again. Visibility is unchanged.",
                        ],
                      ] as const)
                    : []),
                  ...(isEmergency
                    ? ([
                        [
                          "void",
                          "Void this Session",
                          "It stops being supply entirely. Only manually created Sessions can be voided.",
                        ],
                      ] as const)
                    : []),
                  ...(canRetime
                    ? ([
                        [
                          "retime",
                          "Move its start/end time",
                          "Change when this Session runs, still today. Superadmin only.",
                        ],
                      ] as const)
                    : []),
                ] as ReadonlyArray<readonly [Mode, string, string]>
              ).map(([value, label, hint]) => (
                <label
                  key={value}
                  htmlFor={`override-${value}`}
                  className={cn(
                    "flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 transition-colors",
                    mode === value ? "border-primary bg-primary/5" : "border-border",
                  )}
                >
                  <RadioGroupItem id={`override-${value}`} value={value} className="mt-0.5" />
                  <span>
                    <span className="block text-sm font-medium">{label}</span>
                    <span className="block text-xs text-muted-foreground">{hint}</span>
                  </span>
                </label>
              ))}
            </RadioGroup>
          </fieldset>

          {mode === "reassign" ? (
            <>
              <fieldset>
                <legend className="mb-2 block text-sm font-medium">Rider</legend>
                {ridersLoading ? (
                  <div className="flex items-center gap-2 px-1 py-2 text-sm text-muted-foreground">
                    <Spinner className="size-4" />
                    Loading riders in this hub…
                  </div>
                ) : options.length === 0 ? (
                  <p className="rounded-md border border-border px-3 py-2 text-sm text-muted-foreground">
                    No other active Rider in this hub. A Rider from another Dispatch Area cannot
                    hold this Session.
                  </p>
                ) : (
                  <RadioGroup value={riderId} onValueChange={setRiderId}>
                    <div className="max-h-52 space-y-1.5 overflow-y-auto pr-1">
                      {options.map((rider) => (
                        <label
                          key={rider.id}
                          htmlFor={`rider-${rider.id}`}
                          className={cn(
                            "flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 transition-colors",
                            riderId === rider.id ? "border-primary bg-primary/5" : "border-border",
                          )}
                        >
                          <RadioGroupItem id={`rider-${rider.id}`} value={rider.id} />
                          <span className="min-w-0 flex-1 truncate text-sm">{rider.full_name}</span>
                          <Badge variant="outline" className="shrink-0 capitalize">
                            {rider.rider_rank}
                          </Badge>
                        </label>
                      ))}
                    </div>
                  </RadioGroup>
                )}
              </fieldset>

              <fieldset>
                <legend className="mb-2 block text-sm font-medium">
                  Maximum extension for the incoming Rider
                </legend>
                <div className="flex gap-2">
                  {EXTENSION_CHOICES.map((hours) => (
                    <Button
                      key={hours}
                      type="button"
                      variant={extension === hours ? "default" : "outline"}
                      onClick={() => setExtension(hours)}
                      className="flex-1"
                    >
                      {hours}h
                    </Button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Chosen before securing (REND-005), so it belongs to the Rider taking the Session —
                  never carried over from whoever held it.
                </p>
              </fieldset>
            </>
          ) : null}

          {mode === "retime" ? (
            <fieldset className="space-y-3">
              <legend className="mb-2 block text-sm font-medium">New window</legend>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label
                    htmlFor="retime-starts"
                    className="block text-xs font-medium text-muted-foreground"
                  >
                    New start
                  </label>
                  <Input
                    id="retime-starts"
                    type="datetime-local"
                    value={retimeStartsAt}
                    onChange={(e) => setRetimeStartsAt(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label
                    htmlFor="retime-ends"
                    className="block text-xs font-medium text-muted-foreground"
                  >
                    New end
                  </label>
                  <Input
                    id="retime-ends"
                    type="datetime-local"
                    value={retimeEndsAt}
                    onChange={(e) => setRetimeEndsAt(e.target.value)}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                The new start must stay on the Session&rsquo;s own service date — the server refuses
                a different day. Maximum window is 24 hours. Refused once it has started.
              </p>
            </fieldset>
          ) : null}

          <div className="space-y-2">
            <label htmlFor="override-reason" className="block text-sm font-medium">
              Reason (required)
            </label>
            <Textarea
              id="override-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this Session moving? e.g. Rider did not report; backup called in."
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Written to the audit log with your name, and to the Session&rsquo;s own timeline.
            </p>
          </div>

          {/* The rule being overridden, named on the control that overrides it —
              so this cannot be mistaken for an ordinary edit. */}
          <p className="flex gap-2 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-warning">
            <WarningIcon weight="fill" className="mt-px size-4 shrink-0" />
            <span>
              {mode === "retime" ? (
                <>
                  Manual override, Superadmin only. Separate authorization from the actions above —
                  OQ-122 covers ownership, not time; this exists by its own decision. Every use is
                  audited.
                </>
              ) : (
                <>
                  Manual override. CORE-05B normally reserves Session ownership to the Rider who
                  secures it; this exists by client decision (OQ-122) and every use is audited.
                </>
              )}
            </span>
          </p>

          {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant={mode === "reassign" || mode === "retime" ? "default" : "destructive"}
            disabled={!canSubmit}
            onClick={() => {
              if (mode === "release") return onRelease({ reason: trimmedReason });
              if (mode === "void") return onVoid({ reason: trimmedReason });
              if (mode === "retime") {
                return onRetime({
                  startsAt: new Date(retimeStartsAt).toISOString(),
                  endsAt: new Date(retimeEndsAt).toISOString(),
                  reason: trimmedReason,
                });
              }
              return onReassign({
                toRiderId: riderId,
                maxExtensionHours: extension,
                reason: trimmedReason,
              });
            }}
          >
            {isPending
              ? "Saving…"
              : mode === "release"
                ? "Return to pool"
                : mode === "void"
                  ? "Void Session"
                  : mode === "retime"
                    ? "Save new time"
                    : currentHolder
                      ? "Reassign Session"
                      : "Assign Session"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
